import {sanitizeHtml, textToHtml} from '@frontapp/editor-html';
import React, {FC, useMemo} from 'react';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isHtml} from '../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {CalendarEventTimeFormatter} from '../../../../srcCommon/formatters/dateTime/calendarEventTimeFormatter';
import {buildCalendarEventDescriptionHtml} from '../../../../srcCommon/helpers/calendarEventHelpers';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {ArbitraryCalendarEventModel} from '../../../models/api/arbitraryCalendarEventModel';
import {CalendarEventVersionShowAsTypesEnum} from '../../../models/api/calendarEventVersionModel';
import {ArbitraryCalendarEventParticipantModel} from '../../../models/api/visualParticipantModel';
import {ArbitraryCalendarEventParticipantsList} from '../participants/arbitraryCalendarEventParticipantsList';
import {CalendarEventParticipantsSummary} from '../participants/calendarEventParticipantsSummary';
import {CalendarEventDetailRow} from './calendarEventDetailRow';
import {
  maybeRenderConferenceRow,
  maybeRenderDescriptionRow,
  maybeRenderLocationsRow,
  StyledCalendarNameDiv,
  StyledEventColorDiv,
  StyledSummaryDiv,
  StyledSummaryWrapperDiv,
  StyledTextDiv,
} from './calendarEventViewerContentHelpers';

/*
 * Props.
 */

export interface ArbitraryCalendarEventViewerContentProps {
  arbitraryCalendarEvent: ArbitraryCalendarEventModel;
  color: PaletteColorsEnum;
  timeFormat: TimeFormatsEnum;
}

/*
 * Component.
 */

export const ArbitraryCalendarEventViewerContent: FC<ArbitraryCalendarEventViewerContentProps> = (props) => {
  const {arbitraryCalendarEvent, color, timeFormat} = props;

  const {description = ''} = arbitraryCalendarEvent;
  const descriptionHtml = useMemo(
    () =>
      buildCalendarEventDescriptionHtml(
        isHtml(description) ? sanitizeHtml(description) : textToHtml(description),
        undefined,
      ),
    [description],
  );
  const descriptionIsEmpty = useMemo(() => htmlToText(description).trim().length === 0, [description]);

  return (
    <>
      <CalendarEventDetailRow
        iconContent={renderTitleSectionIcon(color)}
        content={renderTitleSectionContent(arbitraryCalendarEvent)}
      />
      <CalendarEventDetailRow
        iconName="snoozeSmall"
        content={
          <StyledTextDiv>
            <CalendarEventTimeFormatter
              timeFormat={timeFormat}
              calendarEventVersion={arbitraryCalendarEvent}
              shouldUseMultiline
            />
          </StyledTextDiv>
        }
      />
      {maybeRenderLocationsRow(arbitraryCalendarEvent)}
      {maybeRenderConferenceRow(arbitraryCalendarEvent)}
      {maybeRenderParticipantsRow(arbitraryCalendarEvent.participants ?? [])}
      {maybeRenderDescriptionRow(descriptionHtml, descriptionIsEmpty)}
      <CalendarEventDetailRow
        iconName="calendarSmall"
        content={renderCalendarContent(arbitraryCalendarEvent)}
      />
    </>
  );
};

function renderTitleSectionIcon(color: PaletteColorsEnum) {
  return <StyledEventColorDiv $color={color} />;
}

function renderTitleSectionContent(arbitraryCalendarEvent: ArbitraryCalendarEventModel) {
  return (
    <StyledSummaryWrapperDiv>
      <StyledSummaryDiv $isCancelled={false}>{renderSummary(arbitraryCalendarEvent)}</StyledSummaryDiv>
    </StyledSummaryWrapperDiv>
  );
}

const untitledEvent = <Trans id="arbitrary-calendar-event-viewer-untitled-event">(Untitled)</Trans>;

function renderSummary(arbitraryCalendarEvent: ArbitraryCalendarEventModel) {
  if (arbitraryCalendarEvent.summary !== undefined) {
    if (arbitraryCalendarEvent.summary === '') {
      return untitledEvent;
    }
    return arbitraryCalendarEvent.summary;
  }

  if (arbitraryCalendarEvent.showAs === CalendarEventVersionShowAsTypesEnum.BUSY) {
    return <Trans id="arbitrary-calendar-event-viewer-busy-event">Busy</Trans>;
  }

  if (arbitraryCalendarEvent.showAs === CalendarEventVersionShowAsTypesEnum.FREE) {
    return <Trans id="arbitrary-calendar-event-viewer-free-event">Free</Trans>;
  }

  return untitledEvent;
}

function renderCalendarContent(arbitraryCalendarEvent: ArbitraryCalendarEventModel) {
  return <StyledCalendarNameDiv>{arbitraryCalendarEvent.address}</StyledCalendarNameDiv>;
}

function maybeRenderParticipantsRow(
  visualParticipants: ReadonlyArray<ArbitraryCalendarEventParticipantModel>,
) {
  const attendees = visualParticipants.filter((p) => p.isAttendee);
  if (attendees.length === 0) {
    return null;
  }

  return <CalendarEventDetailRow iconName="teammatesSmall" content={renderParticipantsContent(attendees)} />;
}

function renderParticipantsContent(attendees: ReadonlyArray<ArbitraryCalendarEventParticipantModel>) {
  return (
    <div>
      <CalendarEventParticipantsSummary visualParticipants={attendees} />
      <ArbitraryCalendarEventParticipantsList visualParticipants={attendees} />
    </div>
  );
}
